import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';

// ============================================================
// Catalogue de formations (côté marchand).
// Le marchand parcourt les cours créés par le superadmin, consulte
// la vidéo / le PDF et s'inscrit à un cours.
// ============================================================

interface Course {
  id: number; titre: string; description: string | null; image: string | null;
  prix: number | null; duree: string | null; categorie: string | null; souscategorie: string | null;
  video_url: string | null; pdf_url: string | null;
}

function youtubeEmbed(url: string): string | null {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function CatalogTab() {
  const { user } = useAuth();
  const [courses, setCourses] = useState<Course[]>([]);
  const [requests, setRequests] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Course | null>(null);
  const [enrolling, setEnrolling] = useState(false);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [courseRes, reqRes] = await Promise.all([
        supabase.from('formations').select('*').eq('status', 'published').order('titre'),
        user ? supabase.from('cours_access_requests').select('cours_id').eq('iduser', user.id) : Promise.resolve({ data: [] }),
      ]);
      setCourses((courseRes.data as Course[]) || []);
      setRequests(new Set(((reqRes.data || []) as { cours_id: number }[]).map((r) => r.cours_id)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return courses;
    return courses.filter((c) => c.titre.toLowerCase().includes(q) || (c.description || '').toLowerCase().includes(q));
  }, [courses, search]);

  const grouped = useMemo(() => {
    const map = new Map<string, Course[]>();
    filtered.forEach((c) => {
      const key = [c.categorie, c.souscategorie].filter(Boolean).join(' › ') || 'Autres';
      const arr = map.get(key) || [];
      arr.push(c);
      map.set(key, arr);
    });
    return Array.from(map.entries());
  }, [filtered]);

  const enroll = async (course: Course) => {
    if (!user) return;
    setEnrolling(true);
    try {
      const { error: e } = await supabase.from('cours_access_requests').insert({
        idcommerce: getCommerceId(user),
        cours_id: course.id,
        iduser: user.id,
        status: 'pending',
        message: '',
      });
      if (e) throw e;
      setRequests((prev) => new Set(prev).add(course.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur lors de l'inscription");
    } finally {
      setEnrolling(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>;
  }

  return (
    <div>
      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
        </div>
      )}

      <div className="relative mb-5 max-w-sm">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher une formation..."
          className="w-full h-10 pl-9 pr-3 rounded-full border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
        />
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 bg-background-50 border border-background-200/70 rounded-xl">
          <i className="ri-graduation-cap-line text-4xl text-foreground-300 block mb-3"></i>
          <p className="text-foreground-600 font-medium">Aucune formation disponible</p>
          <p className="text-xs text-foreground-400 mt-1">Le catalogue sera bientôt enrichi.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {grouped.map(([group, list]) => (
            <div key={group}>
              <h3 className="text-sm font-bold text-foreground-800 mb-3 flex items-center gap-2">
                <i className="ri-folders-line text-accent-500"></i>{group}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((c) => {
                  const enrolled = requests.has(c.id);
                  return (
                    <div key={c.id} className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden flex flex-col">
                      <div className="h-36 bg-background-100 overflow-hidden">
                        {c.image ? (
                          <img src={c.image} alt={c.titre} className="w-full h-full object-cover object-top" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-foreground-300"><i className="ri-play-circle-line text-3xl"></i></div>
                        )}
                      </div>
                      <div className="p-4 flex flex-col flex-1">
                        <h4 className="text-sm font-semibold text-foreground-900 line-clamp-2">{c.titre}</h4>
                        <p className="text-xs text-foreground-500 line-clamp-2 mt-1 flex-1">{c.description || 'Aucune description'}</p>
                        <div className="flex items-center gap-2 mt-2 text-[11px] text-foreground-400">
                          {c.duree && <span><i className="ri-time-line mr-0.5"></i>{c.duree}</span>}
                          <span className="text-sm font-bold text-primary-600 ml-auto">{c.prix && c.prix > 0 ? `${c.prix} MAD` : 'Gratuit'}</span>
                        </div>
                        <div className="mt-3 flex gap-2">
                          <button onClick={() => setSelected(c)} className="flex-1 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-medium hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap">
                            <i className="ri-eye-line mr-1"></i>Détails
                          </button>
                          {enrolled ? (
                            <span className="flex-1 py-2 rounded-full bg-accent-100 text-accent-700 text-xs font-medium flex items-center justify-center gap-1 whitespace-nowrap">
                              <i className="ri-check-line"></i>Inscrit
                            </span>
                          ) : (
                            <button onClick={() => void enroll(c)} disabled={enrolling} className="flex-1 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-medium hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap">
                              <i className="ri-add-line mr-1"></i>S'inscrire
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Détail cours */}
      {selected && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)}></div>
          <div className="relative bg-background-50 rounded-xl w-full max-w-2xl max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200/70 sticky top-0 bg-background-50">
              <h3 className="text-sm font-semibold text-foreground-950 truncate">{selected.titre}</h3>
              <button onClick={() => setSelected(null)} className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"><i className="ri-close-line text-foreground-500"></i></button>
            </div>
            <div className="p-5 space-y-4">
              {selected.video_url && youtubeEmbed(selected.video_url) ? (
                <div className="aspect-video rounded-lg overflow-hidden bg-black">
                  <iframe src={youtubeEmbed(selected.video_url)!} title={selected.titre} className="w-full h-full border-0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                </div>
              ) : selected.video_url ? (
                <a href={selected.video_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-background-100 text-sm text-foreground-700 hover:bg-background-200/70 transition-colors">
                  <i className="ri-video-line text-primary-500"></i>Ouvrir la vidéo du cours
                </a>
              ) : null}

              <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">{selected.description || 'Aucune description.'}</p>

              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-lg font-bold text-primary-600">{selected.prix && selected.prix > 0 ? `${selected.prix} MAD` : 'Gratuit'}</span>
                {selected.duree && <span className="text-xs text-foreground-500"><i className="ri-time-line mr-1"></i>{selected.duree}</span>}
                {selected.pdf_url && (
                  <a href={selected.pdf_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-xs font-medium">
                    <i className="ri-file-pdf-line"></i>Télécharger le support PDF
                  </a>
                )}
              </div>

              {requests.has(selected.id) ? (
                <div className="px-4 py-3 rounded-lg bg-accent-50 text-accent-700 text-sm font-medium flex items-center gap-2">
                  <i className="ri-checkbox-circle-line"></i>Vous êtes inscrit à ce cours.
                </div>
              ) : (
                <button onClick={() => void enroll(selected)} disabled={enrolling} className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50">
                  {enrolling ? <i className="ri-loader-4-line animate-spin"></i> : "S'inscrire à ce cours"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}